import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Chip, Button, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  Switch, CircularProgress, Snackbar, Alert, InputAdornment,
  FormGroup, FormControlLabel, Checkbox, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, TablePagination, Avatar, Tooltip,
  CardContent,
} from '@mui/material';
import { Add, Edit, Search, Refresh } from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { formatDateTime } from '../utils/dateUtils';

const ALL_ROLES = ['superadmin', 'admin', 'manager', 'support'];
const emptyForm = { name: '', phone: '', email: '', password: '', roles: [] };

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getEmployees, { page: 1, per_page: 500 });
    if (res?.code === '200') {
      const empList = (res.employees ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 }));
      setEmployees(empList);
      setTotal(res.total ?? res.employees?.length ?? 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      employees.filter(
        (emp) =>
          !q ||
          emp.name?.toLowerCase().includes(q) ||
          emp.phone?.includes(q) ||
          emp.email?.toLowerCase().includes(q)
      )
    );
    setPage(0);
  }, [search, employees]);

  const openAdd = () => { setEditEmp(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditEmp(row);
    const roles = Array.isArray(row.roles) ? row.roles : (row.roles ? row.roles.split(',').map(r => r.trim()) : []);
    setForm({ name: row.name || '', phone: row.phone || '', email: row.email || '', password: '', roles });
    setDialogOpen(true);
  };

  const toggleRole = (role) => {
    setForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  const handleSave = async () => {
    // Clean phone number
    const cleanPhone = form.phone.trim().replace(/[\s\-\(\)]/g, '');
    
    if (!form.name || !cleanPhone || !form.email || (!editEmp && !form.password)) {
      setSnack({ open: true, msg: 'Please fill all required fields.', sev: 'warning' }); 
      return;
    }
    
    // Validate exactly 10 digits
    if (!/^\d{10}$/.test(cleanPhone)) {
      setSnack({ open: true, msg: 'Phone must be exactly 10 digits.', sev: 'warning' }); 
      return;
    }
    
    if (form.roles.length === 0) {
      setSnack({ open: true, msg: 'Please select at least one role.', sev: 'warning' }); 
      return;
    }
    
    setSaving(true);
    const endpoint = editEmp ? apiList.editEmployee : apiList.addEmployee;
    const payload = editEmp
      ? { id: editEmp.id, name: form.name, phone: cleanPhone, email: form.email, roles: form.roles }
      : { name: form.name, phone: cleanPhone, email: form.email, password: form.password, roles: form.roles };
    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSaving(false);
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: editEmp ? 'Employee updated.' : 'Employee added.', sev: 'success' });
      setDialogOpen(false); fetchEmployees();
    } else { setSnack({ open: true, msg: res?.message || 'Save failed.', sev: 'error' }); }
  };

  const handleToggle = async (row) => {
    const res = await InvokeApi(Config.base_url + apiList.toggleEmployee, { id: row.id, active: !row.is_active });
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: 'Status updated.', sev: 'success' }); fetchEmployees();
    } else { setSnack({ open: true, msg: res?.message || 'Toggle failed.', sev: 'error' }); }
  };

  // Get paginated data
  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Employees</Typography>
          <Typography variant="body2" color="text.secondary">{filtered.length} employee{filtered.length !== 1 ? 's' : ''}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}
          sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
          Add Employee
        </Button>
      </Box>

      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Search name, phone or email…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 280 }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchEmployees()} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['Name', 'Phone', 'Email', 'Roles', 'Active', 'Last Login', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No employees found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((emp) => (
                      <TableRow key={emp.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 34, height: 34, background: 'linear-gradient(135deg,#1a237e,#1565c0)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                              {(emp.name || 'E')[0].toUpperCase()}
                            </Avatar>
                            <Typography fontWeight={600} fontSize={13}>{emp.name || 'Unknown'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{emp.phone || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{emp.email || '—'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(() => {
                              const roleList = Array.isArray(emp.roles) ? emp.roles : (emp.roles ? String(emp.roles).split(',').map(r => r.trim()) : []);
                              return roleList.map(r => (
                                <Chip key={r} label={r} size="small" 
                                  color={r === 'superadmin' ? 'error' : r === 'admin' ? 'primary' : 'default'} />
                              ));
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Switch size="small" checked={!!emp.is_active} onChange={() => handleToggle(emp)} 
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' } }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {emp.last_login ? formatDateTime(emp.last_login) : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(emp)} sx={{ color: '#1565c0' }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filtered.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={pageSize}
              onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
          </>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {editEmp ? 'Edit Employee' : 'Add Employee'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Full Name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField 
                fullWidth 
                size="small" 
                label="Phone * (10 digits)" 
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                inputProps={{ maxLength: 10 }}
                helperText="Enter exactly 10 digits"
              />
            </Grid>
            <Grid item xs={12} sm={editEmp ? 12 : 6}>
              <TextField fullWidth size="small" label="Email *" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            {!editEmp && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Password *" type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Roles *</Typography>
              <FormGroup row>
                {ALL_ROLES.map(role => (
                  <FormControlLabel
                    key={role}
                    control={<Checkbox size="small" checked={form.roles.includes(role)} onChange={() => toggleRole(role)} />}
                    label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{role}</Typography>}
                  />
                ))}
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
